import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { IMAGES } from "@/lib/constants";
import { useI18n } from "@/i18n/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const Navbar = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  const links = [
    { label: t("nav.projet"), id: "projet" },
    { label: t("nav.marraine"), id: "marraine" },
    { label: t("nav.calendrier"), id: "calendrier" },
    { label: t("nav.direct"), id: "direct" },
    { label: t("nav.candidats"), id: "candidats" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-theatre-gold/20" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => document.getElementById("hero")?.scrollIntoView({ behavior: "smooth" })}
          className="flex items-center gap-3 shrink-0"
        >
          <img src={IMAGES.logo} alt="Viva Artist" className="h-11 w-11 object-contain" />
          <span className="font-serif text-xl tracking-wide text-white">
            VIVA <span className="text-theatre-gold">ARTIST</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-xs uppercase tracking-[0.18em] text-neutral-300 hover:text-theatre-gold transition-colors duration-200 whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-preinscription-btn"
            onClick={() => scrollTo("preinscription")}
            className="border border-theatre-gold text-theatre-gold px-4 py-2 text-xs uppercase tracking-[0.18em] hover:bg-theatre-red hover:border-theatre-red hover:text-white transition-colors duration-300 whitespace-nowrap"
          >
            {t("nav.preinscription")}
          </button>
          <LanguageSwitcher compact />
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LanguageSwitcher compact />
          <button data-testid="nav-mobile-toggle" className="text-white" onClick={() => setOpen((o) => !o)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-black/95 border-t border-theatre-gold/20 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-left text-sm uppercase tracking-[0.2em] text-neutral-200"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-preinscription"
            onClick={() => scrollTo("preinscription")}
            className="border border-theatre-gold text-theatre-gold px-5 py-2 text-xs uppercase tracking-[0.2em]"
          >
            {t("nav.preinscription")}
          </button>
        </div>
      )}
    </header>
  );
};
